import { useEffect, useState } from 'react';

interface PlaceholderImageProps {
  src: string;
  alt: string;
  className?: string;
  /** Visible label inside the placeholder if the asset isn't there yet. */
  hint?: string;
}

/**
 * Renders an <img>; if the asset 404s (assets are dropped manually into
 * /public/images later), falls back to a labeled box so the layout still works.
 */
export default function PlaceholderImage({
  src,
  alt,
  className,
  hint,
}: PlaceholderImageProps) {
  const [errored, setErrored] = useState(false);

  // Reset the error flag whenever `src` changes. Without this, a
  // single 404 anywhere on the page would latch this component
  // into placeholder mode forever — e.g. a carousel cycling
  // through `story-image-{01..05}.png` would 404 on slides 02-05,
  // flip `errored` to true, and then never recover when it loops
  // back to slide 01 because the state is still stale from the
  // previous src.
  useEffect(() => {
    setErrored(false);
  }, [src]);

  if (errored) {
    return (
      <div
        role="img"
        aria-label={alt}
        className={`flex items-center justify-center bg-tan/40 text-center text-[11px] uppercase tracking-wider text-text-muted ${className ?? ''}`}
      >
        <span className="px-2">{hint ?? src}</span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setErrored(true)}
    />
  );
}
